import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Plus, Save, Ban, RotateCcw, Clock, Pencil, Trash2 } from 'lucide-react'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { PageHeader } from '@/components/ui/page-header'
import { Dialog } from '@/components/ui/dialog'
import { CurrencyInput } from '@/components/ui/currency-input'
import { useToast } from '@/components/ui/toast'
import AsyncSelect from '@/components/crud/AsyncSelect'
import ServicoEditor from '@/components/contrato/ServicoEditor'
import AditivoModal from '@/components/contrato/AditivoModal'
import AnexoManager from '@/components/contrato/AnexoManager'
import { formatBRL, formatDate } from '@/lib/utils'

const emptyCompra = () => ({
  modalidadeID: '', numeroModalidade: '', numeroProcesso: '',
  dataInicio: '', dataFim: '', municipioID: '', obs: '',
})
const emptyContrato = () => ({
  numeroContrato: '', clienteID: '', dataContrato: '',
  dataVigenciaInicial: '', dataVigenciaFinal: '', valorTotal: '',
  indiceReajusteID: '', assinadoCliente: false, descricao: '', servicos: [],
})

export default function ContratoForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const isNew = !id

  const [servicos, setServicos] = useState([])
  const [modalidades, setModalidades] = useState([])
  const [indices, setIndices] = useState([])

  // Modo novo
  const [compra, setCompra] = useState(emptyCompra())
  const [contratos, setContratos] = useState([emptyContrato()])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Modo edição
  const [graph, setGraph] = useState(null)
  const [loading, setLoading] = useState(!isNew)

  useEffect(() => {
    Promise.all([
      api.get('/servicos', { params: { limit: 100, status: 1 } }),
      api.get('/modalidades', { params: { limit: 100 } }),
      api.get('/indices-reajuste', { params: { limit: 100 } }),
    ]).then(([s, m, i]) => {
      setServicos(s.data.data)
      setModalidades(m.data.data)
      setIndices(i.data.data)
    })
  }, [])

  const loadGraph = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get(`/contratos/${id}`)
      setGraph(data)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (!isNew) loadGraph()
  }, [isNew, loadGraph])

  // ---------------- Modo NOVO ----------------
  function setContratoField(idx, field, val) {
    setContratos((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: val } : c)))
  }
  function addContratoBlock() {
    setContratos((prev) => [...prev, emptyContrato()])
  }
  function removeContratoBlock(idx) {
    setContratos((prev) => prev.filter((_, i) => i !== idx))
  }

  async function salvarNovo() {
    setError('')
    setSaving(true)
    try {
      const payload = {
        compra,
        contratos: contratos.map((c) => ({
          ...c,
          assinadoCliente: c.assinadoCliente ? 1 : 0,
          servicos: (c.servicos || []).filter((s) => s.servicoID),
        })),
      }
      const { data } = await api.post('/contratos', payload)
      const first = data.contratos?.[0]
      toast.success('Contrato criado com sucesso.')
      navigate(first ? `/contrato/${first.contratoID}` : '/contrato')
    } catch (err) {
      const res = err?.response?.data
      setError(res?.errors ? JSON.stringify(res.errors) : (res?.error || 'Erro ao salvar.'))
      toast.error('Não foi possível salvar o contrato.')
    } finally {
      setSaving(false)
    }
  }

  // Enquanto carrega, ou logo após navegar de /novo para /:id (graph ainda nulo),
  // evita renderizar a EditView com dados ausentes (tela branca).
  if (loading || (!isNew && !graph)) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader backTo="/contrato" backLabel="Contratos" crumb="Detalhe" title="Carregando..." />
        <div className="p-6 text-sm text-muted-foreground">Carregando dados do contrato...</div>
      </div>
    )
  }

  const refs = { servicos, modalidades, indices }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        backTo="/contrato"
        backLabel="Contratos"
        crumb={isNew ? 'Novo' : `Processo ${graph.compra.numeroProcesso}`}
        title={isNew ? 'Novo contrato' : `Processo ${graph.compra.numeroProcesso}`}
        subtitle={isNew
          ? 'Cadastre a compra e os contratos vinculados.'
          : `${graph.compra.modalidadeNome || 'Modalidade'} nº ${graph.compra.numeroModalidade}`}
        meta={isNew ? [] : [
        //   { label: 'Município', value: `${graph.compra.municipioNome}/${graph.compra.estadoUf}` },
        //   { label: 'Vigência', value: `${formatDate(graph.compra.dataInicio)} — ${formatDate(graph.compra.dataFim)}` },
        //   { label: 'Contratos', value: graph.contratos.length },
        ]}
        actions={isNew && (
          <Button onClick={salvarNovo} disabled={saving}>
            <Save className="h-4 w-4" /> {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        )}
      />

      <div className="flex-1 overflow-auto p-6 space-y-5">
        {error && <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">{error}</div>}

        {isNew ? (
          <>
            <CompraCard value={compra} onChange={setCompra} refs={refs} />
            {contratos.map((c, idx) => (
              <ContratoCardNew
                key={idx}
                index={idx}
                value={c}
                onField={(f, v) => setContratoField(idx, f, v)}
                onRemove={contratos.length > 1 ? () => removeContratoBlock(idx) : null}
                refs={refs}
              />
            ))}
            <Button variant="outline" onClick={addContratoBlock}>
              <Plus className="h-4 w-4" /> Adicionar contrato
            </Button>
          </>
        ) : (
          <EditView graph={graph} refs={refs} reload={loadGraph} />
        )}
      </div>
    </div>
  )
}

/* =================== CARDS =================== */

function CompraCard({ value, onChange, refs, readOnly, initialMunicipioLabel }) {
  const set = (f, v) => onChange({ ...value, [f]: v })
  return (
    <Card className="p-5">
      <h2 className="font-semibold mb-4">Compra</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Field label="Modalidade" required>
          <Select value={value.modalidadeID} onChange={(e) => set('modalidadeID', e.target.value)} disabled={readOnly}>
            <option value="">Selecione...</option>
            {refs.modalidades.map((m) => <option key={m.modalidadeID} value={m.modalidadeID}>{m.nome}</option>)}
          </Select>
        </Field>
        <Field label="Nº Modalidade" required>
          <Input value={value.numeroModalidade} onChange={(e) => set('numeroModalidade', e.target.value)} disabled={readOnly} />
        </Field>
        <Field label="Nº Processo" required>
          <Input value={value.numeroProcesso} onChange={(e) => set('numeroProcesso', e.target.value)} disabled={readOnly} />
        </Field>
        <Field label="Data início" required>
          <Input type="date" value={value.dataInicio || ''} onChange={(e) => set('dataInicio', e.target.value)} disabled={readOnly} />
        </Field>
        <Field label="Data fim" required>
          <Input type="date" value={value.dataFim || ''} onChange={(e) => set('dataFim', e.target.value)} disabled={readOnly} />
        </Field>
        <Field label="Município" required>
          <AsyncSelect endpoint="/municipios" value={value.municipioID} onChange={(v) => set('municipioID', v)}
            optionValue="municipioID" optionLabel="nome" initialLabel={initialMunicipioLabel || ''} />
        </Field>
        <Field label="Observação" className="col-span-2 lg:col-span-3">
          <Input value={value.obs || ''} onChange={(e) => set('obs', e.target.value)} disabled={readOnly} />
        </Field>
      </div>
    </Card>
  )
}

function ContratoFields({ value, set, refs, initialClienteLabel }) {
  const somaServicos = (value.servicos || []).reduce((s, r) => s + Number(r.quantidade || 0) * Number(r.valorUnitario || 0), 0)
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <Field label="Nº Contrato" required>
        <Input value={value.numeroContrato} onChange={(e) => set('numeroContrato', e.target.value)} />
      </Field>
      <Field label="Cliente" required>
        <AsyncSelect endpoint="/clientes" value={value.clienteID} onChange={(v) => set('clienteID', v)}
          optionValue="clienteID" optionLabel="nome" initialLabel={initialClienteLabel || ''} />
      </Field>
      <Field label="Índice de reajuste" required>
        <Select value={value.indiceReajusteID} onChange={(e) => set('indiceReajusteID', e.target.value)}>
          <option value="">Selecione...</option>
          {refs.indices.map((i) => <option key={i.indiceReajusteID} value={i.indiceReajusteID}>{i.nome}</option>)}
        </Select>
      </Field>
      <Field label="Data do contrato (assinatura)" required>
        <Input type="date" value={value.dataContrato || ''} onChange={(e) => set('dataContrato', e.target.value)} />
      </Field>
      <Field label="Vigência inicial" required>
        <Input type="date" value={value.dataVigenciaInicial || ''} onChange={(e) => set('dataVigenciaInicial', e.target.value)} />
      </Field>
      <Field label="Vigência final" required>
        <Input type="date" value={value.dataVigenciaFinal || ''} onChange={(e) => set('dataVigenciaFinal', e.target.value)} />
      </Field>
      <Field label="Valor total (R$)">
        <div className="flex gap-2">
          <CurrencyInput value={value.valorTotal} onChange={(v) => set('valorTotal', v)} />
          {value.servicos && (
            <Button type="button" variant="outline" size="sm" title="Usar soma dos serviços" onClick={() => set('valorTotal', somaServicos.toFixed(2))}>=Σ</Button>
          )}
        </div>
      </Field>
      <Field label="Assinado pelo cliente">
        <label className="flex items-center gap-2 h-10">
          <input type="checkbox" checked={!!value.assinadoCliente} onChange={(e) => set('assinadoCliente', e.target.checked)} className="h-4 w-4 accent-[var(--primary)]" />
          <span className="text-sm text-muted-foreground">Sim</span>
        </label>
      </Field>
      <Field label="Descrição" className="col-span-2 lg:col-span-3">
        <Input value={value.descricao || ''} onChange={(e) => set('descricao', e.target.value)} />
      </Field>
    </div>
  )
}

function ContratoCardNew({ index, value, onField, onRemove, refs }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold">Contrato {index + 1}</h2>
        {onRemove && <Button variant="ghost" size="sm" onClick={onRemove}>Remover</Button>}
      </div>
      <ContratoFields value={value} set={onField} refs={refs} />
      <div className="mt-4">
        <Label className="mb-2 block">Serviços iniciais (aditivo marco zero)</Label>
        <ServicoEditor value={value.servicos} onChange={(rows) => onField('servicos', rows)} servicos={refs.servicos} />
      </div>
    </Card>
  )
}

/* =================== MODO EDIÇÃO =================== */

function EditView({ graph, refs, reload }) {
  const toast = useToast()
  const [compra, setCompra] = useState(() => normalizeCompra(graph.compra))
  const [savingCompra, setSavingCompra] = useState(false)

  useEffect(() => setCompra(normalizeCompra(graph.compra)), [graph])

  async function salvarCompra() {
    setSavingCompra(true)
    try {
      await api.put(`/compras/${graph.compra.compraID}`, compra)
      await reload()
      toast.success('Compra atualizada.')
    } catch {
      toast.error('Erro ao salvar a compra.')
    } finally {
      setSavingCompra(false)
    }
  }

  return (
    <>
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Compra</h2>
          <Button size="sm" onClick={salvarCompra} disabled={savingCompra}><Save className="h-4 w-4" /> Salvar</Button>
        </div>
        <CompraInner value={compra} onChange={setCompra} refs={refs} initialMunicipioLabel={graph.compra.municipioNome} />
      </Card>

      {graph.contratos.map((c) => (
        <ContratoCardEdit key={c.contratoID} contrato={c} compraID={graph.compra.compraID} refs={refs} reload={reload} />
      ))}
    </>
  )
}

function CompraInner({ value, onChange, refs, initialMunicipioLabel }) {
  const set = (f, v) => onChange({ ...value, [f]: v })
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <Field label="Modalidade" required>
        <Select value={value.modalidadeID} onChange={(e) => set('modalidadeID', e.target.value)}>
          <option value="">Selecione...</option>
          {refs.modalidades.map((m) => <option key={m.modalidadeID} value={m.modalidadeID}>{m.nome}</option>)}
        </Select>
      </Field>
      <Field label="Nº Modalidade" required><Input value={value.numeroModalidade} onChange={(e) => set('numeroModalidade', e.target.value)} /></Field>
      <Field label="Nº Processo" required><Input value={value.numeroProcesso} onChange={(e) => set('numeroProcesso', e.target.value)} /></Field>
      <Field label="Data início" required><Input type="date" value={value.dataInicio || ''} onChange={(e) => set('dataInicio', e.target.value)} /></Field>
      <Field label="Data fim" required><Input type="date" value={value.dataFim || ''} onChange={(e) => set('dataFim', e.target.value)} /></Field>
      <Field label="Município" required>
        <AsyncSelect endpoint="/municipios" value={value.municipioID} onChange={(v) => set('municipioID', v)}
          optionValue="municipioID" optionLabel="nome" initialLabel={initialMunicipioLabel || ''} />
      </Field>
      <Field label="Observação" className="col-span-2 lg:col-span-3"><Input value={value.obs || ''} onChange={(e) => set('obs', e.target.value)} /></Field>
    </div>
  )
}

function ContratoCardEdit({ contrato, refs, reload }) {
  const toast = useToast()
  const [form, setForm] = useState(() => normalizeContrato(contrato))
  const [saving, setSaving] = useState(false)
  const [aditivoOpen, setAditivoOpen] = useState(false)
  const [aditivoEditando, setAditivoEditando] = useState(null)
  const [aditivoExcluir, setAditivoExcluir] = useState(null)
  const [inativarOpen, setInativarOpen] = useState(false)
  const set = (f, v) => setForm((prev) => ({ ...prev, [f]: v }))

  useEffect(() => setForm(normalizeContrato(contrato)), [contrato])

  async function salvar() {
    setSaving(true)
    try {
      await api.put(`/contratos/${contrato.contratoID}`, { ...form, assinadoCliente: form.assinadoCliente ? 1 : 0 })
      await reload()
      toast.success('Contrato atualizado.')
    } catch {
      toast.error('Erro ao salvar o contrato.')
    } finally {
      setSaving(false)
    }
  }

  function abrirNovoAditivo() {
    setAditivoEditando(null)
    setAditivoOpen(true)
  }
  function abrirEdicaoAditivo(a) {
    setAditivoEditando(a)
    setAditivoOpen(true)
  }

  async function salvarAditivo(payload) {
    if (aditivoEditando) {
      await api.put(`/aditivos/${aditivoEditando.aditivoID}`, payload)
    } else {
      await api.post(`/contratos/${contrato.contratoID}/aditivos`, payload)
    }
    setAditivoOpen(false)
    await reload()
    toast.success(aditivoEditando ? 'Aditivo atualizado.' : 'Aditivo adicionado.')
  }

  const baseServicos = contrato.aditivos?.find((a) => a.servicos?.length > 0)?.servicos || []
  // A timeline vem em ordem decrescente: só o primeiro item (último aditivo) é excluível,
  // e nunca o marco zero (quando é o único do contrato).
  const ultimoAditivoID = contrato.aditivos?.length > 1 ? contrato.aditivos[0].aditivoID : null

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="font-semibold">Contrato {contrato.numeroContrato}</h2>
          {contrato.ativo ? <Badge variant="success">Ativo</Badge> : <Badge variant="muted">Inativo: {contrato.motivoInativacao}</Badge>}
          {contrato.diasParaAditivar !== null && (
            <span className="text-xs text-muted-foreground">Vigência até {formatDate(contrato.dataFimVigente)} · {contrato.diasParaAditivar} dias</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {contrato.ativo ? (
              <Button size="sm" variant="outline" onClick={() => setInativarOpen(true)}><Ban className="h-4 w-4" /> Inativar</Button>
            ) : (
                <Button size="sm" variant="outline" onClick={async () => { await api.put(`/contratos/${contrato.contratoID}/reativar`); await reload(); toast.success('Contrato reativado.') }}><RotateCcw className="h-4 w-4" /> Reativar</Button>
            )}
            <Button size="sm" onClick={abrirNovoAditivo}><Plus className="h-4 w-4" /> Aditivo</Button>
          <Button size="sm" onClick={salvar} disabled={saving}><Save className="h-4 w-4" /> Salvar</Button>
        </div>
      </div>

      <ContratoFields value={form} set={set} refs={refs} initialClienteLabel={contrato.clienteNome} />

      {/* Anexos do contrato */}
      <AnexoManager
        anexos={contrato.anexos}
        idKey="contratoAnexoID"
        uploadUrl={`/contratos/${contrato.contratoID}/anexos`}
        itemBase="/contrato-anexos"
        reload={reload}
      />

      {/* Timeline de aditivos */}
      <div className="mt-6">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3"><Clock className="h-4 w-4" /> Linha do tempo de aditivos</h3>
        <div className="space-y-3">
          {contrato.aditivos.map((a) => (
            <div key={a.aditivoID} className="border-l-2 border-primary/40 pl-4 relative">
              <div className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-primary" />
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={a.tipo === 'prazo' ? 'default' : 'success'}>{a.tipo}</Badge>
                <span className="text-sm">{formatDate(a.dataInicio)} — {formatDate(a.dataFim)}</span>
                {a.assinado ? <span className="text-xs text-emerald-600">assinado</span> : <span className="text-xs text-muted-foreground">não assinado</span>}
                {a.observacao && <span className="text-xs text-muted-foreground">· {a.observacao}</span>}
                <button
                  onClick={() => abrirEdicaoAditivo(a)}
                  title="Editar aditivo"
                  className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setAditivoExcluir(a)}
                  disabled={a.aditivoID !== ultimoAditivoID}
                  title={motivoBloqueioExclusao(a, contrato.aditivos, ultimoAditivoID) || 'Excluir aditivo'}
                  className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {a.servicos?.length > 0 && (
                <div className="mt-1.5 text-sm">
                  {a.servicos.map((s) => (
                    <div key={s.aditivoServicoID} className="flex items-center gap-2 text-muted-foreground">
                      <span className="text-foreground">{s.servicoNome}</span>
                      <span>· {s.quantidade} × {formatBRL(s.valorUnitario)} = {formatBRL(s.valorTotal)}</span>
                      {!!s.fatura && <Badge variant="default" className="text-[10px]">fatura</Badge>}
                    </div>
                  ))}
                </div>
              )}
              <AnexoManager
                anexos={a.anexos}
                idKey="aditivoAnexoID"
                uploadUrl={`/aditivos/${a.aditivoID}/anexos`}
                itemBase="/aditivo-anexos"
                reload={reload}
                compact
              />
            </div>
          ))}
        </div>
      </div>

      <AditivoModal
        open={aditivoOpen}
        onClose={() => setAditivoOpen(false)}
        onSubmit={salvarAditivo}
        servicos={refs.servicos}
        baseServicos={baseServicos}
        aditivo={aditivoEditando}
      />
      <ExcluirAditivoModal aditivo={aditivoExcluir} onClose={() => setAditivoExcluir(null)} reload={reload} />
      <InativarModal open={inativarOpen} onClose={() => setInativarOpen(false)} contratoID={contrato.contratoID} reload={reload} />
    </Card>
  )
}

/** Confirmação de exclusão do último aditivo (remove serviços e anexos junto). */
function ExcluirAditivoModal({ aditivo, onClose, reload }) {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { if (aditivo) setError('') }, [aditivo])

  async function confirmar() {
    setSaving(true)
    try {
      await api.delete(`/aditivos/${aditivo.aditivoID}`)
      onClose()
      await reload()
      toast.success('Aditivo excluído.')
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao excluir o aditivo.')
      toast.error('Não foi possível excluir o aditivo.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!aditivo} onClose={onClose} title="Excluir aditivo" size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancelar</Button><Button variant="destructive" onClick={confirmar} disabled={saving}>{saving ? 'Excluindo...' : 'Excluir'}</Button></>}>
      {aditivo && (
        <div className="space-y-2 text-sm">
          <p>
            Excluir o aditivo <strong>{aditivo.tipo}</strong> de {formatDate(aditivo.dataInicio)} a {formatDate(aditivo.dataFim)}?
          </p>
          <p className="text-muted-foreground">
            Os {aditivo.servicos?.length || 0} serviço(s) e {aditivo.anexos?.length || 0} anexo(s) vinculados também serão excluídos. Esta ação não pode ser desfeita.
          </p>
          {error && <p className="text-destructive">{error}</p>}
        </div>
      )}
    </Dialog>
  )
}

function InativarModal({ open, onClose, contratoID, reload }) {
  const toast = useToast()
  const [motivo, setMotivo] = useState('Finalizado')
  const [saving, setSaving] = useState(false)
  async function confirmar() {
    setSaving(true)
    try {
      await api.put(`/contratos/${contratoID}/inativar`, { motivoInativacao: motivo })
      onClose()
      await reload()
      toast.success('Contrato inativado.')
    } catch {
      toast.error('Erro ao inativar o contrato.')
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog open={open} onClose={onClose} title="Inativar contrato" size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancelar</Button><Button variant="destructive" onClick={confirmar} disabled={saving}>Inativar</Button></>}>
      <Field label="Motivo">
        <Select value={motivo} onChange={(e) => setMotivo(e.target.value)}>
          <option value="Finalizado">Finalizado</option>
          <option value="Cancelado">Cancelado</option>
        </Select>
      </Field>
    </Dialog>
  )
}

/* =================== HELPERS =================== */

/** Por que a lixeira do aditivo está desabilitada (null = pode excluir). */
function motivoBloqueioExclusao(aditivo, aditivos, ultimoAditivoID) {
  if (aditivo.aditivoID === ultimoAditivoID) return null
  if ((aditivos?.length || 0) <= 1) return 'O aditivo marco zero do contrato não pode ser excluído.'
  return 'Somente o último aditivo do contrato pode ser excluído.'
}

function Field({ label, required, children, className = '' }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label>{label}{required && <span className="text-destructive"> *</span>}</Label>
      {children}
    </div>
  )
}

function normalizeCompra(c) {
  return {
    modalidadeID: c.modalidadeID ?? '', numeroModalidade: c.numeroModalidade ?? '',
    numeroProcesso: c.numeroProcesso ?? '', dataInicio: (c.dataInicio || '').slice(0, 10),
    dataFim: (c.dataFim || '').slice(0, 10), municipioID: c.municipioID ?? '', obs: c.obs ?? '',
  }
}
function normalizeContrato(c) {
  return {
    numeroContrato: c.numeroContrato ?? '', clienteID: c.clienteID ?? '',
    dataContrato: (c.dataContrato || '').slice(0, 10),
    dataVigenciaInicial: (c.dataVigenciaInicial || '').slice(0, 10),
    dataVigenciaFinal: (c.dataVigenciaFinal || '').slice(0, 10),
    valorTotal: c.valorTotal ?? '', indiceReajusteID: c.indiceReajusteID ?? '',
    assinadoCliente: !!c.assinadoCliente, descricao: c.descricao ?? '',
  }
}
